import { useEffect, useState } from "react";
import commonClient from "@apis/commonClient";

/**
 * Whether the UI is running against the demo deployment (STIC-15481 R8).
 *
 * Fetched once per page load and shared, because it is asked for by both the login
 * page and the app shell and it cannot change while the tab is open. Answering from a
 * module-level promise keeps that to a single request instead of one per mount.
 *
 * A failure never rejects: it resolves to the hostname guess, so not knowing which
 * environment this is costs at most a wrong banner, never a broken page.
 */
let pending: Promise<boolean> | null = null;

/**
 * Optimistic first answer, so the bar is there on the first paint instead of dropping in
 * a moment later and pushing the whole layout down 40px on every page load.
 *
 * The hostname is only a guess and deliberately not the source of truth: the same
 * deployment answers on several names — demo.sensolus.com, internaldemo.sensolus.com and
 * internaldemoadmin.sensolus.com are one platform (verified: all three serve the
 * demo-csrf cookie) — and the last of those does not contain the substring. The server
 * decides from its own hostIpAddress and corrects this a moment later; guessing wrong
 * only costs the flicker this exists to avoid.
 */
const guessFromHostname = (): boolean =>
  typeof window !== "undefined" && window.location.hostname.includes("demo.sensolus.com");

const fetchIsDemo = (): Promise<boolean> => {
  if (!pending) {
    pending = commonClient
      .isDemoEnvironment()
      .then(({ data }: { data: unknown }) => data === true || data === "true")
      // Falling back to false would drop the banner on a transient failure even where the
      // hostname plainly says demo, which is the one direction that must not happen.
      .catch(() => guessFromHostname());
  }
  return pending;
};

export const useDemoEnvironment = (): boolean => {
  const [isDemo, setIsDemo] = useState(guessFromHostname);

  useEffect(() => {
    let alive = true;
    fetchIsDemo().then((value) => {
      if (alive) {
        setIsDemo(value);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return isDemo;
};

export default useDemoEnvironment;
